import { boxFromLayout } from '@lessonup/pin-renderer';
import { TeacherPin, Vector } from '@lessonup/pins-shared';
import { PlayerStore } from '../../playerStore.types';

export const CATEGORY_FALLBACK = 'uncategorized';

export function areAllAnswerByCategoryAnswersRevealed(store: PlayerStore): boolean {
  const currentPinId = store.currentPinId();
  if (!currentPinId) {
    return false;
  }

  const currentPinState = store.currentPinState();
  if (!currentPinState || !currentPinState.answerByCategory) {
    return false;
  }

  const answerByCategoryData = currentPinState.answerByCategory;

  if (!answerByCategoryData.answers || answerByCategoryData.answers.length === 0) {
    return true;
  }
  if (!answerByCategoryData.revealedAnswers) {
    return false;
  }
  if (answerByCategoryData.answers.length > answerByCategoryData.revealedAnswers.length) {
    return false;
  }
  const revealedAnswersSet = new Set(answerByCategoryData.revealedAnswers);
  return answerByCategoryData.answers.every((answer) =>
    revealedAnswersSet.has(keyForAnswer(answer.text, answer.category))
  );
}

export function keyForAnswer(normalizedAnswer: string, categoryId: string = CATEGORY_FALLBACK): string {
  return `${normalizedAnswer}-${categoryId}`;
}

/**
 * Detects which category a position is over, if any.
 * Supports both AUTO and MANUAL layout modes.
 *
 * @param position - The position to check
 * @param currentPin - The current pin with category area component
 * @returns The category ID if position is over a category, null otherwise
 */
export function detectCategory(position: Vector, currentPin: TeacherPin | null): string | null {
  if (!currentPin?.pinComponents) return null;

  const categoryAreaComponent = currentPin.pinComponents.find((component) => component.type === 'CATEGORY_AREA');

  if (!categoryAreaComponent) return null;

  const containerBox = boxFromLayout(categoryAreaComponent.layout);

  if (!('categories' in categoryAreaComponent.settings)) {
    return null;
  }

  const settings = categoryAreaComponent.settings;
  const categories = settings.categories || [];
  const layoutMode = settings.layoutMode || 'AUTO';

  if (
    position.x < containerBox.position.x ||
    position.x > containerBox.position.x + containerBox.size.width ||
    position.y < containerBox.position.y ||
    position.y > containerBox.position.y + containerBox.size.height
  ) {
    return null;
  }

  for (const category of categories) {
    let categoryPosition;

    if (layoutMode === 'AUTO') {
      const categoryIndex = categories.findIndex((c) => c.id === category.id);
      const categoriesPerRow = Math.max(1, Math.floor((containerBox.size.width - 16) / 130));
      const row = Math.floor(categoryIndex / categoriesPerRow);
      const col = categoryIndex % categoriesPerRow;

      categoryPosition = {
        x: containerBox.position.x + 8 + col * 130,
        y: containerBox.position.y + 8 + row * 60,
      };
    } else {
      categoryPosition = {
        x: containerBox.position.x + category.layout.position.x,
        y: containerBox.position.y + category.layout.position.y,
      };
    }

    const categorySize = category.layout.size;

    if (
      position.x >= categoryPosition.x &&
      position.x <= categoryPosition.x + categorySize.width &&
      position.y >= categoryPosition.y &&
      position.y <= categoryPosition.y + categorySize.height
    ) {
      return category.id;
    }
  }

  return null;
}
